import type { Interpolation, Theme } from '@emotion/react';
import {
  Box,
  COLOR_PALETTE,
  Divider,
  Icon,
  type IconProps,
  Indicator,
  NavLink,
  type NavLinkProps,
} from '@inflearn/ds-react-next';
import { KoOnly } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { faHeadSideSpeak } from 'pro-regular-svg-icons-v7';
import { GameButton } from '../../../../components/pages/games/entry/GameButton';
import { AI_INTERVIEWS_URL } from '../../../../constants/urls';
import { useSidebarLinks } from './useSidebarLinks';

export function Sidebar({
  styles,
}: {
  styles?: {
    root?: Interpolation<Theme>;
  };
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const sidebarLinks = useSidebarLinks();

  return (
    <Box css={styles?.root}>
      {sidebarLinks.map(({ isActive: checkIsActive, ...link }) => {
        const isActive = checkIsActive(router.asPath);

        return <SidebarNavLink key={link.label} {...link} active={isActive} />;
      })}
      <KoOnly>
        <Divider my={12} />
        <SidebarNavLink
          label={
            <Indicator position="middle-end" color="red" processing inline offset={-16}>
              {t('ai_interviews.entry_point.lnb_label')}
            </Indicator>
          }
          // pro-regular-svg-icons-v7는 별칭 설치라 IconDefinition이 ds-icon-next(FA6.6) 타입과 다른 버전으로 잡힘 — 런타임 구조는 동일
          icon={
            <Icon icon={faHeadSideSpeak as unknown as IconProps['icon']} size="lg" fixedWidth />
          }
          href={AI_INTERVIEWS_URL.LANDING({ from: 'profile' })}
          isExternal={false}
        />
      </KoOnly>
      <GameButton />
    </Box>
  );
}

function SidebarNavLink(props: NavLinkProps & { isExternal?: boolean; href: string }) {
  const router = useRouter();
  const { isExternal, href, active, ...restProps } = props;

  const navLinkStyles = {
    root: {
      padding: '12px 16px',
      height: '49px',
      borderRadius: '8px',
    },
    label: {
      fontSize: '16px',
      fontWeight: 400,
    },
    icon: {
      color: active ? COLOR_PALETTE.infgreen[8] : COLOR_PALETTE.gray[7],
    },
  };

  if (isExternal) {
    return (
      <NavLink
        component="a"
        href={href}
        target="_blank"
        variant="light"
        styles={navLinkStyles}
        active={active}
        {...restProps}
      />
    );
  }

  return (
    <NavLink
      component="a"
      target="_self"
      href={href}
      variant="light"
      styles={navLinkStyles}
      active={active}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        // Allow opening in new tab with Cmd/Ctrl + click
        if (e.metaKey || e.ctrlKey) {
          return;
        }
        e.preventDefault();
        router.push(href);
      }}
      {...restProps}
    />
  );
}
